import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import CommandLayout from "@/components/CommandLayout";
import AdminAnalyticsCard from "@/components/admin/AdminAnalyticsCard";
import { Loader2, Sparkles, Upload, ArrowLeft, FlaskConical, RefreshCw, Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface BacklogItem {
  id: string;
  source: "content_library" | "neutralizations";
  created_at?: string;
  toxic_message: string;
  title: string | null;
  teaser_text: string | null;
  analysis_logic: string | null;
  neutralization_script: string | null;
  toxicity_score: number;
  invideo_prompt: string | null;
  category: string | null;
  workflow_status: string;
  source_status: string;
  linked_neutralization_id?: string | null;
}

interface GeneratedCard {
  title: string;
  teaser_text: string;
  analysis_logic: string;
  neutralization_script: string;
  toxicity_score: number;
  invideo_prompt: string;
}

interface PreviewCardData extends GeneratedCard {
  category: string | null;
}

const ADMIN_EMAIL = "admin@greyrockai.app";

const AdminLab = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [selected, setSelected] = useState<BacklogItem | null>(null);
  const [generated, setGenerated] = useState<GeneratedCard | null>(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const previewCard = useMemo<PreviewCardData | null>(() => {
    if (!selected) return null;

    if (generated) {
      return {
        ...generated,
        category: selected.category,
      };
    }

    return {
      title: selected.title || "Pending forensic title",
      teaser_text: selected.teaser_text || "Queued from the live analyzer.",
      analysis_logic: selected.analysis_logic || selected.teaser_text || "Generate the forensic card to expand this analysis before publishing.",
      neutralization_script: selected.neutralization_script || "No neutralization script has been generated yet.",
      toxicity_score: Number(selected.toxicity_score || 0),
      invideo_prompt: selected.invideo_prompt || "",
      category: selected.category,
    };
  }, [generated, selected]);

  // Wait for auth to fully load before checking access
  useEffect(() => {
    if (authLoading) return; // Still loading — do nothing
    if (!user || user.email !== ADMIN_EMAIL) {
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) fetchBacklog();
  }, [user]);

  // Show loading screen while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan" />
      </div>
    );
  }

  // Block render for non-admins (redirect will fire from useEffect)
  if (!user || user.email !== ADMIN_EMAIL) {
    return null;
  }

  const fetchBacklog = async () => {
    setLoading(true);
    const [contentResult, neutralizationResult] = await Promise.all([
      supabase
        .from("content_library")
        .select("id, toxic_message, title, teaser_text, analysis_logic, neutralization_script, toxicity_score, invideo_prompt, category, workflow_status, created_at")
        .eq("workflow_status", "backlog")
        .order("created_at", { ascending: false }),
      supabase
        .from("neutralizations")
        .select("id, raw_text, subtext_decoder, grey_rock_response, toxicity_score, manipulation_primary_type, content_status, created_at")
        .eq("content_status", "backlog")
        .order("created_at", { ascending: false }),
    ]);

    const error = contentResult.error || neutralizationResult.error;

    if (error) {
      toast({ title: "Error loading backlog", description: error.message, variant: "destructive" });
    } else {
      const contentItems: BacklogItem[] = (contentResult.data || []).map((item) => ({
        id: item.id,
        source: "content_library",
        created_at: item.created_at,
        toxic_message: item.toxic_message,
        title: item.title,
        teaser_text: item.teaser_text,
        analysis_logic: item.analysis_logic,
        neutralization_script: item.neutralization_script,
        toxicity_score: Number(item.toxicity_score || 0),
        invideo_prompt: item.invideo_prompt,
        category: item.category,
        workflow_status: item.workflow_status,
        source_status: item.workflow_status,
        linked_neutralization_id: null,
      }));

      const contentMessageSet = new Set(contentItems.map((item) => item.toxic_message.trim().toLowerCase()));

      const neutralizationItems: BacklogItem[] = (neutralizationResult.data || [])
        .filter((item) => !contentMessageSet.has(item.raw_text.trim().toLowerCase()))
        .map((item) => ({
          id: item.id,
          source: "neutralizations",
          created_at: item.created_at,
          toxic_message: item.raw_text,
          title: null,
          teaser_text: item.subtext_decoder,
          analysis_logic: item.subtext_decoder,
          neutralization_script: item.grey_rock_response,
          toxicity_score: Number(item.toxicity_score || 0),
          invideo_prompt: null,
          category: item.manipulation_primary_type,
          workflow_status: item.content_status,
          source_status: item.content_status,
          linked_neutralization_id: item.id,
        }));

      const nextItems = [...contentItems, ...neutralizationItems].sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );

      setItems(nextItems);
      setSelected((current) => {
        if (!current) return nextItems[0] ?? null;
        return nextItems.find((item) => item.id === current.id && item.source === current.source) ?? nextItems[0] ?? null;
      });
    }
    setLoading(false);
    setInitialLoad(false);
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true);
    setGenerated(null);

    try {
      const { data, error } = await supabase.functions.invoke("generate-forensic-card", {
        body: { toxic_message: selected.toxic_message },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setGenerated(data as GeneratedCard);
      toast({ title: "✅ Forensic card generated" });
    } catch (e: any) {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!selected || !generated) return;
    setPublishing(true);

    try {
      const updates: Array<Promise<{ error: Error | null }>> = [];

      if (selected.source === "content_library") {
        updates.push(
          Promise.resolve(
            supabase
            .from("content_library")
            .update({
              user_id: null,
              workflow_status: "published",
              title: generated.title,
              teaser_text: generated.teaser_text,
              analysis_logic: generated.analysis_logic,
              neutralization_script: generated.neutralization_script,
              toxicity_score: generated.toxicity_score,
              invideo_prompt: generated.invideo_prompt,
            })
            .eq("id", selected.id)
            .then(({ error }) => ({ error: error ? new Error(error.message) : null }))
          )
        );
      }

      if (selected.source === "neutralizations" || selected.linked_neutralization_id) {
        updates.push(
          Promise.resolve(
            supabase
            .from("neutralizations")
            .update({ content_status: "approved" })
            .eq("id", selected.linked_neutralization_id || selected.id)
            .then(({ error }) => ({ error: error ? new Error(error.message) : null }))
          )
        );
      }

      if (selected.source === "neutralizations") {
        updates.push(
          Promise.resolve(
            supabase
            .from("content_library")
            .insert({
              user_id: null,
              workflow_status: "published",
              toxic_message: selected.toxic_message,
              title: generated.title,
              teaser_text: generated.teaser_text,
              analysis_logic: generated.analysis_logic,
              neutralization_script: generated.neutralization_script,
              toxicity_score: generated.toxicity_score,
              invideo_prompt: generated.invideo_prompt,
              category: generated.title ? selected.category : null,
            })
            .then(({ error }) => ({ error: error ? new Error(error.message) : null }))
          )
        );
      }

      const results = await Promise.all(updates);
      const failed = results.find((result) => result.error);
      if (failed?.error) throw failed.error;

      toast({ title: "🚀 Published to Library!" });
      setSelected(null);
      setGenerated(null);
      fetchBacklog();
    } catch (e: any) {
      toast({ title: "Publish failed", description: e.message, variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  if (authLoading || !user || user.email !== ADMIN_EMAIL) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <CommandLayout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <FlaskConical className="w-5 h-5 text-cyan" />
          <h1 className="text-xl font-bold tracking-tight">Admin Lab</h1>
          <Badge variant="outline" className="text-xs border-cyan/30 text-cyan">
            {items.length} in backlog
          </Badge>
        </div>

        {/* Featured: Behavioral Intelligence / Attribution Console */}
        <AdminAnalyticsCard />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT: Backlog List */}
          <Card className="border-border bg-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Backlog Queue
                </CardTitle>
                <Button variant="outline" size="sm" onClick={fetchBacklog} disabled={loading}>
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Loading…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-1" /> Force Refresh</>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[60vh]">
                {(loading && initialLoad) ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : items.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-12">No backlog items</p>
                ) : (
                  <div className="divide-y divide-border">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => { setSelected(item); setGenerated(null); }}
                        className={cn(
                          "w-full text-left px-4 py-3 transition-colors hover:bg-secondary/50",
                          selected?.id === item.id && "bg-cyan/5 border-l-2 border-l-cyan"
                        )}
                      >
                        <p className="text-sm font-medium text-foreground line-clamp-2">
                          {item.title || item.toxic_message.slice(0, 80) + "…"}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {item.category && (
                            <Badge variant="secondary" className="text-[10px]">{item.category}</Badge>
                          )}
                          <span className="text-[10px] text-muted-foreground font-mono">
                            score: {Number(item.toxicity_score).toFixed(1)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* RIGHT: Selected Item + Generation */}
          <div className="space-y-4">
            {!selected ? (
              <Card className="border-border bg-card">
                <CardContent className="py-16 text-center">
                  <p className="text-sm text-muted-foreground">Select a backlog item to process</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Toxic Message Preview */}
                <Card className="border-border bg-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                      Raw Toxic Message
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap font-mono bg-secondary/30 rounded-lg p-3">
                      {selected.toxic_message}
                    </p>
                    <Button
                      onClick={handleGenerate}
                      disabled={generating}
                      className="mt-4 w-full bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground"
                    >
                      {generating ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating…</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Generate Forensic Card</>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Generated Result */}
                {previewCard && (
                  <Card className="border-primary/20 bg-card">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-bold text-primary">9:16 Forensic Card Preview</CardTitle>
                        <Badge className={cn(
                          "text-[10px] font-mono",
                          previewCard.toxicity_score >= 7
                            ? "bg-destructive/20 text-destructive"
                            : previewCard.toxicity_score >= 4
                              ? "bg-amber-500/20 text-amber-400"
                              : "bg-muted text-muted-foreground"
                        )}>
                          {previewCard.toxicity_score.toFixed(1)}/10
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="mx-auto w-full max-w-[320px] overflow-hidden rounded-xl border border-border bg-background shadow-lg">
                        <div className="border-b border-border px-4 py-3">
                          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                            <span>GreyRock AI®</span>
                            <span>Forensic Analysis</span>
                          </div>
                        </div>

                        <div className="border-b border-border px-5 py-5 text-center">
                          <p className="text-sm italic leading-relaxed text-primary">
                            “{previewCard.teaser_text}”
                          </p>
                        </div>

                        <div className="border-b border-border px-5 py-6 text-center">
                          <h3 className="text-2xl font-black uppercase tracking-tight text-foreground">
                            {previewCard.title}
                          </h3>
                          {previewCard.category && (
                            <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
                              {previewCard.category}
                            </p>
                          )}
                          <div className="mt-3 flex items-center justify-center gap-2">
                            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">
                              Forensic Score:
                            </span>
                            <span className="text-xl font-black font-mono text-primary">
                              {previewCard.toxicity_score.toFixed(1)}/10
                            </span>
                          </div>
                        </div>

                        <div className="px-5 py-4">
                          <p className="mb-3 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                            Forensic Analysis
                          </p>
                          <div className="rounded-md border-l-2 border-primary bg-secondary/30 px-4 py-3">
                            <p className="text-xs leading-relaxed text-muted-foreground line-clamp-6">
                              {previewCard.analysis_logic}
                            </p>
                          </div>
                        </div>

                        <div className="border-t border-border px-5 py-5">
                          <div className="flex items-center justify-center gap-3 text-muted-foreground">
                            <Lock className="w-4 h-4" />
                            <span className="text-[11px] font-mono uppercase tracking-[0.2em]">
                              Full Response Locked
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Teaser</p>
                        <p className="text-sm text-foreground/80">{previewCard.teaser_text}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Forensic Analysis</p>
                        <p className="text-sm text-foreground/80 leading-relaxed">{previewCard.analysis_logic}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Neutralization Strategy</p>
                        <p className="text-sm text-foreground/80 leading-relaxed">{previewCard.neutralization_script}</p>
                      </div>
                      {previewCard.invideo_prompt && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">TikTok Hook</p>
                          <p className="text-xs text-muted-foreground italic leading-relaxed">{previewCard.invideo_prompt}</p>
                        </div>
                      )}

                      <Button
                        onClick={handlePublish}
                        disabled={publishing || !generated}
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        {publishing ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Publishing…</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" /> Publish to Library</>
                        )}
                      </Button>
                      {!generated && (
                        <p className="text-xs text-muted-foreground text-center">
                          Generate the forensic card first to validate the final 9:16 preview before publishing.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </CommandLayout>
  );
};

export default AdminLab;
